<template>
  <hb-section :title="$t('influencer_panel.tab_statement')">
    <template #subtitle>
      <span>
        {{ $t('influencer_panel.inflows') }}
        <hb-money :cents="totals.inflow_cents" size="sm" tone="positive" />
        ·
        {{ $t('influencer_panel.outflows') }}
        <hb-money :cents="totals.outflow_cents" size="sm" />
      </span>
    </template>

    <template #actions>
      <b-form-select
        :value="type"
        :options="typeOptions"
        size="sm"
        class="statement__filter"
        :aria-label="$t('influencer_panel.entry_type')"
        @change="$emit('update:type', $event)"
      />
    </template>

    <hb-empty-state v-if="!entries.length" :title="$t('influencer_panel.no_entries')" icon="FileTextIcon" />

    <div v-else>
      <div v-for="entry in entries" :key="entry.id" class="statement__row">
        <span class="statement__icon" :class="`statement__icon--${entry.direction === 'CR' ? 'in' : 'out'}`">
          <feather-icon :icon="entry.direction === 'CR' ? 'ArrowDownLeftIcon' : 'ArrowUpRightIcon'" size="15" />
        </span>

        <div class="statement__identity">
          <div class="statement__title">{{ entry.type_label }}</div>
          <div class="statement__subtitle">
            <span v-if="entry.description">{{ entry.description }} · </span>{{ shortDate(entry.created_at) }}
          </div>
        </div>

        <div class="statement__badge">
          <b-badge v-if="isPending(entry)" variant="light-warning">
            {{ $t('influencer_panel.entry_status_pending') }}
          </b-badge>
          <b-badge v-else-if="entry.direction === 'CR'" variant="light-success">
            {{ $t('influencer_panel.entry_status_available') }}
          </b-badge>
        </div>

        <div class="statement__amount">
          <hb-money :cents="entry.signed_amount_cents" signed tone="auto" />
        </div>
      </div>

      <!-- O endpoint sempre paginou; a tela antiga so lia a primeira pagina e o resto do
           extrato era inalcancavel. -->
      <b-pagination
        v-if="lastPage > 1"
        :value="currentPage"
        :total-rows="total"
        :per-page="perPage"
        align="center"
        class="mt-2 mb-0"
        @change="$emit('update:page', $event)"
      />
    </div>
  </hb-section>
</template>

<script>
import { BBadge, BFormSelect, BPagination } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import { shortDate, parseApiDate } from '../panel'

/**
 * Extrato da carteira, com filtro por tipo e paginacao.
 *
 * Nao ha coluna de status no lancamento: "em carencia" e derivado de `spendable_at` estar
 * no futuro, que e a mesma regra que o WalletBalanceReader usa para separar os saldos.
 */
export default {
  name: 'StatementPane',
  components: {
    BBadge, BFormSelect, BPagination, HbSection, HbMoney, HbEmptyState,
  },
  props: {
    entries: { type: Array, default: () => [] },
    totals: { type: Object, default: () => ({ inflow_cents: 0, outflow_cents: 0 }) },
    type: { type: String, default: '' },
    currentPage: { type: Number, default: 1 },
    lastPage: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    perPage: { type: Number, default: 30 },
  },
  computed: {
    /** Os codigos e rotulos sao os de `WalletEntry::TYPE_LABELS` no backend. */
    typeOptions() {
      return [
        { value: '', text: this.$t('influencer_panel.all_types') },
        { value: 'CM', text: 'Comissão' },
        { value: 'CE', text: 'Estorno de comissão' },
        { value: 'CO', text: 'Compra na plataforma' },
        { value: 'CD', text: 'Devolução de compra' },
        { value: 'SQ', text: 'Saque solicitado' },
        { value: 'SE', text: 'Saque estornado' },
        { value: 'BN', text: 'Bônus' },
        { value: 'AJ', text: 'Ajuste' },
      ]
    },
  },
  methods: {
    shortDate,
    isPending(entry) {
      if (entry.direction !== 'CR' || !entry.spendable_at) return false

      const spendableAt = parseApiDate(entry.spendable_at)

      return !!spendableAt && spendableAt.getTime() > Date.now()
    },
  },
}
</script>

<style scoped>
.statement__filter {
  width: auto;
  min-width: 170px;
}

.statement__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  padding: 0.8rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.statement__row:last-of-type {
  border-bottom: none;
}

.statement__icon {
  flex: none;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-pill);
}

.statement__icon--in {
  background: var(--tint-success);
  color: var(--success);
}

.statement__icon--out {
  background: var(--tint-secondary);
  color: var(--secondary);
}

.statement__identity {
  flex: 1 1 200px;
  min-width: 0;
}

.statement__title {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.statement__subtitle {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.statement__badge {
  flex: 0 0 auto;
}

.statement__amount {
  flex: 0 0 auto;
  margin-left: auto;
  font-weight: var(--fw-medium);
}
</style>
